// Plan 23b, Task 2.2 — Comic Text Bubble primitive
// Jagged SVG star bubble + direct GSAP entrance animation + injectable onomatopoeia hook.

import gsap from 'gsap';
import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ComicWord = 'BOOM' | 'POW' | 'ZAP' | 'CRACK' | 'WHOOSH';

export interface ComicBubbleOpts {
  word: ComicWord;
  mode: 'static' | 'animated';
  /** Default: random value in [-12, +12]. */
  rotationDeg?: number;
  /** If provided, position element absolutely at this x pixel coordinate. */
  screenX?: number;
  /** If provided, position element absolutely at this y pixel coordinate. */
  screenY?: number;
  /** Font size of the text. Default: 32px (typography.size.comic-text). */
  sizePx?: number;
  /** Optional injectable hook for 3D VFX side effect in animated mode. */
  onomatopoeiaHook?: { play(el: HTMLElement, opts?: unknown): GsapTimelineHandle };
}

export interface ComicBubbleHandle extends UIComponentHandle {
  /** In mode:'animated' plays the pop-in + exit animation and fires the hook.
   *  In mode:'static' is a no-op. */
  triggerEntrance(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** animation.duration.fast = 150ms expressed in seconds for GSAP. */
const FAST_SEC = 0.15;

/** animation.easing.bouncy from tokens.json */
const BOUNCY_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** typography.size.comic-text = 32px */
const DEFAULT_SIZE_PX = 32;

/** z-index.comic-bubbles = 180 */
const Z_COMIC = 180;

/** typography.outline.comic — 4-corner text-shadow technique from tokens.json */
const COMIC_OUTLINE =
  '-3px -3px 0 #000000, 3px -3px 0 #000000, -3px 3px 0 #000000, 3px 3px 0 #000000';

// ─────────────────────────────────────────────────────────────────────────────
// Word color table — exact values from docs/design/tokens.json color.comic.*
// ─────────────────────────────────────────────────────────────────────────────

interface WordColors {
  bubble: string;
  text: string;
  outline: string;
}

const WORD_COLORS: Record<ComicWord, WordColors> = {
  BOOM: { bubble: '#D94040', text: '#FFE020', outline: '#1A0A00' },
  POW: { bubble: '#4A8ED9', text: '#FFFFFF', outline: '#0A1A30' },
  ZAP: { bubble: '#9040C0', text: '#FFFFFF', outline: '#1A0030' },
  CRACK: { bubble: '#8B4513', text: '#FFE020', outline: '#1A0A00' },
  WHOOSH: { bubble: '#5CAD40', text: '#FFFFFF', outline: '#0A1A00' },
};

// ─────────────────────────────────────────────────────────────────────────────
// SVG jagged star polygon points generator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generate a star polygon with irregular spiky points.
 * Uses fixed per-point outer radius offsets to ensure visual consistency
 * while maintaining the "spiky star" appearance required by spec.
 *
 * viewBox="0 0 100 100", cx=50, cy=50
 */
function generateStarPolygonPoints(): string {
  const N = 12; // 12 star tips (24 total polygon vertices: outer + inner alternating)
  const cx = 50;
  const cy = 50;
  const innerR = 22;
  const baseOuterR = 46;

  // Fixed per-tip outer radius offsets for irregular/spiky appearance.
  // Indexed by tip index (0..N-1). Keeps the shape deterministic per render.
  const outerOffsets = [0, 4, -3, 6, -2, 5, -4, 3, 2, -5, 4, -3];

  const points: string[] = [];

  for (let i = 0; i < N * 2; i++) {
    const angle = (i * Math.PI) / N - Math.PI / 2;
    let r: number;
    if (i % 2 === 0) {
      // Outer vertex — apply per-tip irregular offset
      r = baseOuterR + outerOffsets[i / 2];
    } else {
      // Inner vertex — constant inner radius
      r = innerR;
    }
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    points.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }

  return points.join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createComicBubble(config: UIComponentConfig & ComicBubbleOpts): ComicBubbleHandle {
  const {
    gsapRegistry,
    mount,
    word,
    mode,
    rotationDeg,
    screenX,
    screenY,
    sizePx = DEFAULT_SIZE_PX,
    onomatopoeiaHook,
  } = config;

  let disposed = false;
  let entranceHandle: GsapTimelineHandle | null = null;

  // Resolve rotation: use provided value or generate random in [-12, +12]
  const resolvedRotation =
    rotationDeg !== undefined ? rotationDeg : Math.round((Math.random() * 24 - 12) * 10) / 10;

  const colors = WORD_COLORS[word];

  // ── Build DOM ───────────────────────────────────────────────────────────────

  // Root element
  const root = document.createElement('div');
  root.className = 'comic-bubble';

  const isAbsolute = screenX !== undefined && screenY !== undefined;
  root.style.cssText = [
    `position: ${isAbsolute ? 'absolute' : 'relative'}`,
    isAbsolute ? `left: ${screenX}px` : '',
    isAbsolute ? `top: ${screenY}px` : '',
    `display: inline-flex`,
    `align-items: center`,
    `justify-content: center`,
    `transform: rotate(${resolvedRotation}deg)`,
    `z-index: ${Z_COMIC}`,
    `user-select: none`,
    `pointer-events: none`,
  ]
    .filter(Boolean)
    .join('; ');

  // SVG jagged star bubble
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('width', `${sizePx * 3}`);
  svg.setAttribute('height', `${sizePx * 3}`);
  svg.style.cssText = [
    `position: absolute`,
    `top: 0`,
    `left: 0`,
    `width: 100%`,
    `height: 100%`,
  ].join('; ');

  const polygon = document.createElementNS(svgNS, 'polygon');
  polygon.setAttribute('points', generateStarPolygonPoints());
  polygon.setAttribute('fill', colors.bubble);
  polygon.setAttribute('stroke', colors.outline);
  polygon.setAttribute('stroke-width', '3');

  svg.appendChild(polygon);

  // Wrapper to set explicit pixel size for the component
  root.style.width = `${sizePx * 3}px`;
  root.style.height = `${sizePx * 3}px`;
  // Override position after width/height set (cssText replaced above, re-apply)
  root.style.position = isAbsolute ? 'absolute' : 'relative';
  root.style.transform = `rotate(${resolvedRotation}deg)`;

  // Text element — overlays the SVG
  const textEl = document.createElement('span');
  textEl.className = 'comic-bubble__text';
  textEl.textContent = word;
  textEl.style.cssText = [
    // typography.family.headline
    `font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)`,
    // typography.weight.extra-bold = 800
    `font-weight: 800`,
    // Color from word table
    `color: ${colors.text}`,
    `text-transform: uppercase`,
    `font-size: ${sizePx}px`,
    // typography.shadow.comic + typography.outline.comic (4-corner technique) combined
    `text-shadow: 0 3px 0 #000000, 0 6px 8px rgba(0,0,0,0.50), ${COMIC_OUTLINE}`,
    `position: relative`,
    `z-index: 1`,
    `letter-spacing: 1px`,
  ].join('; ');

  root.appendChild(svg);
  root.appendChild(textEl);

  // ── Mount helper ─────────────────────────────────────────────────────────────

  if (mount) {
    mount.appendChild(root);
  }

  // ── triggerEntrance ──────────────────────────────────────────────────────────

  function removeFromParent(): void {
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  function triggerEntrance(): void {
    if (disposed || mode === 'static') return;

    // Kill any prior entrance in-flight
    if (entranceHandle) {
      entranceHandle.dispose();
      entranceHandle = null;
    }

    const handle = gsapRegistry.create('comic-bubble:entrance');
    const tl = handle.timeline;

    // Step 1: Pop-in — scale 0 → 1.25 (position 0)
    tl.fromTo(
      root,
      { scale: 0 },
      { scale: 1.25, duration: FAST_SEC, ease: BOUNCY_EASE },
      0, // F20a-CLASS-1: explicit position
    );

    // Step 2: Settle — scale 1.25 → 1.0 (position FAST_SEC)
    tl.to(
      root,
      { scale: 1.0, duration: FAST_SEC * 0.5, ease: 'power2.out' },
      FAST_SEC, // F20a-CLASS-1: explicit position
    );

    // Step 3: Hold 600ms — zero-duration spacer tween at settle end point
    // (FAST_SEC + FAST_SEC * 0.5 = 0.225s)
    // We use a to({duration: 0.6}) tween that animates nothing significant,
    // acting as a time spacer at the correct position
    const holdStart = FAST_SEC + FAST_SEC * 0.5;
    tl.to(
      root,
      { duration: 0.6, ease: 'none' },
      holdStart, // F20a-CLASS-1: explicit position
    );

    // Step 4: Exit fade (position = holdStart + 0.6)
    const exitStart = holdStart + 0.6;
    tl.to(
      root,
      { opacity: 0, scale: 0.8, duration: FAST_SEC, ease: 'power2.in' },
      exitStart, // F20a-CLASS-1: explicit position
    );

    // Step 5: Remove from parent after exit completes
    const totalSec = exitStart + FAST_SEC;
    tl.call(removeFromParent, undefined, totalSec); // F20a-CLASS-1: explicit position

    entranceHandle = handle;

    // Fire injectable hook for 3D VFX side effect
    onomatopoeiaHook?.play(root, undefined);
  }

  // ── dispose ──────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill any in-flight entrance animation
    if (entranceHandle) {
      entranceHandle.dispose();
      entranceHandle = null;
    }

    // Kill any direct gsap animations on the root element
    gsap.killTweensOf(root);

    // Detach from DOM if mounted
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return {
    element: root,
    triggerEntrance,
    dispose,
  };
}
